import React, { useState, useMemo, useCallback, useContext } from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import PropTypes from "prop-types";
import {
  renderTooltipHeader,
  createColumnMenu,
  renderLabelWithInfoTooltipHeader,
} from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import {
  clearDetailStateFromStorage,
  EnergyBankerEmptyStateStaffer,
  EnergyBankerNoDealOnFilterState,
  getAccordionRowRenderForStafferDeals,
  getFilteredSortedData2,
  getFlattenedData,
  getInitialDetailState,
  ProjectIdCellWithNewBadgeEnergyDealResource,
  saveDetailStateToStorage,
  useGroupToggle2
} from "../../dashboard-energy-banker/energy-banker-time-log/energy-banker-time-log-helper";
import DealTeamAvatarGroupCell from "../../../dashboard-util/dashboard-telerik-helper/deal-team-avatar";
import { isFilteredColumn } from "../../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import "./../../dashboard-energy-banker/energy-banker-time-log/energy-banker-time-log.scss";
import DashboardEnergyStafferDealResourceLogHeader from "./dashboard-energy-staffer-deal-resource-log-header";
import { LoginContext } from "../../../../../services/api/authentication-api";
import DealResourceModal from "./deal-resource-modal";
import { FluentTooltip } from "../../../../../components/FluentUI/Fluent-Tooltip";
import { FluentBadge } from "../../../../../components/FluentUI/Fluent-Badge";

const DashboardEnergyStafferDealResourceLog = ({
  energyStafferDealResourceLogItems = {},
  fetchStafferDealDetailsandResourcingLog,
  setIsEnergyStafferDealResourceLogLoading,
  fetchEnergyTechStafferTimeLog,
  setIsEnergyStafferTimeLogLoading,
  showToast,
  isSI = false,
  fetchEnergyJuniorStaffingIndex,
  setIsEnergyJuniorStaffingIndexLoading,
}) => {
  const user = useContext(LoginContext);
  const [dealState, setDealState] = useState(() => getInitialDetailState(user?.userID, "resourceDealLog"));
  const { groupState, toggleGroup, setGroupState } = useGroupToggle2();
  const [show, setShow] = useState(false);
  const [selectedDealResource, setSelectedDealResource] = useState(null);
  const canEdit = user?.currentUser?.privileges?.["Projects"]?.Edit ?? false;
  const flattenedData = useMemo(() =>
    getFlattenedData(energyStafferDealResourceLogItems?.Deals), [energyStafferDealResourceLogItems?.Deals]);
  const filteredData = useMemo(() =>
    getFilteredSortedData2(flattenedData, dealState.filter, dealState.sort, energyStafferDealResourceLogItems?.SectorNames), [flattenedData, dealState.filter, dealState.sort]);
  const visibleData = useMemo(() => filteredData, [filteredData]);
  const visibleLength = useMemo(() => visibleData?.length, [visibleData?.length]);
  const columnMenu = createColumnMenu(flattenedData);
  const AccordionRowRender = useMemo(() => getAccordionRowRenderForStafferDeals(filteredData, groupState, toggleGroup, 7, energyStafferDealResourceLogItems?.EmptySectors, energyStafferDealResourceLogItems?.SectorNames, dealState.filter), [filteredData, groupState]);
  const isEmpty = visibleData?.length === 0;
  const finalGridData = isEmpty ? [{ isEmptyRow: true, showSearchEmptyState: false, isFilterEmptyState: (dealState.filter?.filters?.length ?? 0) > 0 }] : visibleData;
  const [expandClass, setExpandClass] = useState("");
  const columnIsFiltered = useCallback(
    (fieldName) => isFilteredColumn(fieldName, dealState.filter),
    [dealState.filter]
  );

  const onHide = () => {
    setShow(false);
    setSelectedDealResource(null);
  };
  const onRowClick = (event) => {
    setSelectedDealResource(event.dataItem);
    setShow(true);
  };
  const resetDataState = () => {
    const resetState = { sort: [], filter: null };
    setDealState(resetState);
    clearDetailStateFromStorage(user?.userID, "resourceDealLog");
  };

  const expandWidget = () => {
    setExpandClass(expandClass === "" ? "widgetExpanded" : "");
  };

  return (
    <div className={`energy-banker-time-log clickable-row ${expandClass} dashboard-container-shadow dashboard-energy-staffer-deal-resource-log`}>
      <DashboardEnergyStafferDealResourceLogHeader
        energyStafferDealResourceLogItems={energyStafferDealResourceLogItems}
        user={user}
        resetDataState={resetDataState}
        dealState={dealState}
        expandWidget={expandWidget}
        expandClass={expandClass}
        isSI={isSI}
      />
      <div className="detail-grid-scroll-container energy-staffer-widgets" data-testid="energy-grid">
        <Grid
          data={finalGridData}
          dataItemKey={"ProjectId"}
          sortable={true}
          sort={dealState.sort}
          filter={dealState.filter}
          onSortChange={(e) => {
            setDealState({ ...dealState, sort: e.sort })
            saveDetailStateToStorage(user.userID, { ...dealState, sort: e.sort }, "", "resourceDealLog");
            setGroupState({ ...groupState });
          }}
          onFilterChange={(e) => {
            saveDetailStateToStorage(user.userID, { ...dealState, filter: e.filter }, "", "resourceDealLog");
            setDealState({ ...dealState, filter: e.filter })
          }}
          onRowClick={onRowClick}
          rowRender={AccordionRowRender}
          resizable={true}
        >
          <Column
            field="ProjectId"
            title={renderTooltipHeader("#")}
            filter="text"
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("ProjectId") ? "filtered" : ""}
            cell={(props) =>
              energyStafferDealResourceLogItems?.Deals?.length > 0
                ? props.dataItem.isFilterEmptyState
                  ? <td colSpan={7} className=" k-table-td "><EnergyBankerNoDealOnFilterState resetDataState={resetDataState} /></td>
                  : <ProjectIdCellWithNewBadgeEnergyDealResource {...props} sectorNames={energyStafferDealResourceLogItems?.SectorNames} canEdit={canEdit && props.dataItem.IsProjectMember} isNewDeal={props.dataItem.IsNewDeal} />
                : <td colSpan={7}>
                  <EnergyBankerEmptyStateStaffer canCreate={user?.currentUser?.privileges?.["Projects"]?.Add ?? false} />
                </td>
            }
          />
          <Column
            field="ProjectName"
            title={renderTooltipHeader("Deal Name")}
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("ProjectName") ? "filtered" : ""}
            cell={(props) => (
              (energyStafferDealResourceLogItems?.Deals?.length > 0 &&  !props.dataItem.isFilterEmptyState) &&
              <FluentTooltip
                target={<td data-testid="project-name-column" className="background-white k-table-td"> {props.dataItem.ProjectName}{props.dataItem.IsOverdue}   {" "}
                  {props.dataItem.IsOverdue && (<FluentBadge
                    appearance="filled"
                    size="small"
                    color="warning"
                    text="Overdue"
                    shape="rounded"
                  />)}
                </td>}
                text={props.dataItem.ProjectName}>
              </FluentTooltip>
            )}
          />
          <Column
            field="Company"
            title={renderTooltipHeader("Company")}
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("Company") ? "filtered" : ""}
            cell={(props) => (
                  (energyStafferDealResourceLogItems?.Deals?.length > 0 &&  !props.dataItem.isFilterEmptyState) &&
              <FluentTooltip
                target={<td className="background-white k-table-td"> {props.dataItem.Company}</td>}
                text={props.dataItem.Company}>
              </FluentTooltip>
            )}
          />
          <Column
            field="Live"
            title={renderTooltipHeader("Live/Non-Live")}
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("Live") ? "filtered" : ""}
            cell={(props) => (
                 (energyStafferDealResourceLogItems?.Deals?.length > 0 &&  !props.dataItem.isFilterEmptyState) &&
              <FluentTooltip
                target={<td className="background-white k-table-td"> {props.dataItem.Live}</td>}
                text={props.dataItem.Live}>
              </FluentTooltip>
            )}
          />
          <Column
            field="CurrentWeekHours"
            title={renderLabelWithInfoTooltipHeader("Current Wk Hrs", "Avg. per Junior Banker")}
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("CurrentWeekHours") ? "filtered" : ""}
            cell={(props) => (
                  (energyStafferDealResourceLogItems?.Deals?.length > 0 &&  !props.dataItem.isFilterEmptyState) &&
              <td className="summary-cell k-table-td" >
                {props.dataItem.CurrentWeekHours ?? "—"}
              </td>
            )}
          />
          <Column
            field="Description"
            title={renderTooltipHeader("Description")}
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("Description") ? "filtered" : ""}
            cell={(props) => (
                   (energyStafferDealResourceLogItems?.Deals?.length > 0 &&  !props.dataItem.isFilterEmptyState) &&
              <FluentTooltip
                target={<td className="background-white k-table-td"> {props.dataItem.Description}</td>}
                text={props.dataItem.Description}>
              </FluentTooltip>
            )}
          />
          <Column
            field="Members"
            title={renderTooltipHeader(isSI ? "Interns" : "Deal Team")}
            filter="text"
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("Members") ? "filtered" : ""}
            cell={(props) =>
                  (energyStafferDealResourceLogItems?.Deals?.length > 0 &&  !props.dataItem.isFilterEmptyState) && <td className="background-white k-table-td" >
                {props.dataItem.Members?.length
                  ? <DealTeamAvatarGroupCell members={props.dataItem.Members} />
                  : "-"
                }
              </td>
            }
          />
        </Grid>
      </div>
      {show && (
        <DealResourceModal
          show={show}
          onHide={onHide}
          item={selectedDealResource}
          sectorNames={energyStafferDealResourceLogItems?.SectorNames}
          visibleLength={visibleLength}
          fetchStafferDealDetailsandResourcingLog={fetchStafferDealDetailsandResourcingLog}
          setIsEnergyStafferDealResourceLogLoading={setIsEnergyStafferDealResourceLogLoading}
          fetchEnergyTechStafferTimeLog={fetchEnergyTechStafferTimeLog}
          setIsEnergyStafferTimeLogLoading={setIsEnergyStafferTimeLogLoading}
          fetchEnergyJuniorStaffingIndex={fetchEnergyJuniorStaffingIndex}
          setIsEnergyJuniorStaffingIndexLoading={setIsEnergyJuniorStaffingIndexLoading}
          showToast={showToast}
          isSI={isSI}
        />
      )}
    </div>
  );
};

DashboardEnergyStafferDealResourceLog.propTypes = {
  energyStafferDealResourceLogItems: PropTypes.array,
  fetchStafferDealDetailsandResourcingLog: PropTypes.func,
  setIsEnergyStafferDealResourceLogLoading: PropTypes.func,
  fetchEnergyTechStafferTimeLog: PropTypes.func,
  setIsEnergyStafferTimeLogLoading: PropTypes.func,
  showToast: PropTypes.func,
  isSI: PropTypes.bool,
  fetchEnergyJuniorStaffingIndex: PropTypes.func,
  setIsEnergyJuniorStaffingIndexLoading: PropTypes.func,
};

export default DashboardEnergyStafferDealResourceLog;
